import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
    <div className="hidden lg:flex flex-col justify-between bg-green text-cream p-12 relative grain overflow-hidden">
      <Link to="/" className="relative z-10 flex items-center gap-2.5">
        <span className="inline-block w-2.5 h-2.5 bg-ochre rotate-45" />
        <span className="font-display font-bold text-lg">FundReady Lab</span>
      </Link>
      <div className="relative z-10">
        <p className="font-serif-accent text-2xl leading-snug text-cream/95">
          Find out what's actually standing between your organization and the money it needs.
        </p>
        <p className="mt-4 text-cream/60 text-sm">An honest look at your fundraising.</p>
      </div>
      <div />
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-bold text-navy">{title}</h1>
        <p className="mt-2 text-muted-foreground">{subtitle}</p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const field = "w-full px-4 py-3 bg-white border border-line rounded-[3px] text-charcoal focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold transition";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const redirect = new URLSearchParams(location.search).get("redirect") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u.role === "admin" ? "/admin" : redirect);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <Shell title="Log in" subtitle="Access your dashboard and saved reports.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {err && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-[3px]" data-testid="login-error">{err}</div>}
        <input className={field} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
        <input className={field} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm text-gold hover:text-gold-dark">Forgot password?</Link>
        </div>
        <button disabled={loading} className="btn-gold w-full font-semibold py-3.5 rounded-[3px]" data-testid="login-submit">
          {loading ? "Logging in…" : "Log in"}
        </button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">
        No account? <Link to="/register" className="text-navy font-semibold" data-testid="link-register">Create one</Link>
      </p>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const redirect = new URLSearchParams(location.search).get("redirect") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created");
      navigate(redirect);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <Shell title="Create your account" subtitle="Save your assessment, unlock your Blueprint, and return anytime.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {err && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-[3px]" data-testid="register-error">{err}</div>}
        <input className={field} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" />
        <input className={field} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" />
        <input className={field} type="password" placeholder="Password (min 6 characters)" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} required data-testid="register-password" />
        <button disabled={loading} className="btn-gold w-full font-semibold py-3.5 rounded-[3px]" data-testid="register-submit">
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">
        Already have an account? <Link to="/login" className="text-navy font-semibold" data-testid="link-login">Log in</Link>
      </p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const api = (await import("../lib/api")).default;
    await api.post("/auth/forgot-password", { email });
    setSent(true);
  };
  return (
    <Shell title="Reset your password" subtitle="We'll send a secure reset link if an account exists.">
      {sent ? (
        <div className="p-4 bg-forest/10 border border-forest/30 text-forest rounded-[3px]" data-testid="forgot-success">
          If an account exists for {email}, a reset link has been sent. (In this preview, the link is printed to the server logs.)
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <input className={field} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" />
          <button className="btn-gold w-full font-semibold py-3.5 rounded-[3px]" data-testid="forgot-submit">Send reset link</button>
        </form>
      )}
      <p className="mt-6 text-sm text-muted-foreground"><Link to="/login" className="text-navy font-semibold">Back to login</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const api = (await import("../lib/api")).default;
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated");
      navigate("/login");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <Shell title="Set a new password" subtitle="Enter a new password for your account.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        {err && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-[3px]">{err}</div>}
        <input className={field} type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} required data-testid="reset-password" />
        <button className="btn-gold w-full font-semibold py-3.5 rounded-[3px]" data-testid="reset-submit">Update password</button>
      </form>
    </Shell>
  );
}
